import clsx from "clsx";
import React from "react";
import {
  Dialog,
  DateRangePicker as HeadlessDateRangePicker,
  type DateValue,
} from "react-aria-components";

// Relative imports (not the "@appsmith/wds" barrel) keep this component out
// of the package-barrel dependency cycle counted by the CI cyclic-deps gate.
import { inputFieldStyles } from "../../Field";
import { FieldError } from "../../FieldError";
import { FieldLabel } from "../../FieldLabel";
import { Popover, useRootContainer } from "../../Popover";

import type { DateRangePickerProps } from "./types";
import { RangeCalendar } from "./RangeCalendar";
import dateRangePickerStyles from "./styles.module.css";
import { DateRangePickerTrigger } from "./DateRangePickerTrigger";

/**
 * DateRangePicker wraps RAC's DateRangePicker in the exact Field chrome
 * idiom of Datepicker (FieldLabel / FieldError / contextual help / isLoading
 * suffix spinner). v1 is day-granularity only — no TimeField in the popover.
 */
export const DateRangePicker = <T extends DateValue>(
  props: DateRangePickerProps<T>,
) => {
  const {
    className,
    contextualHelp,
    errorMessage,
    isDisabled,
    isLoading,
    isRequired,
    label,
    popoverClassName,
    size = "medium",
    ...rest
  } = props;
  const root = useRootContainer();

  return (
    <HeadlessDateRangePicker
      aria-label={Boolean(label) ? undefined : "DateRangePicker"}
      className={clsx(inputFieldStyles.field, className)}
      data-size={size}
      isDisabled={isDisabled}
      isRequired={isRequired}
      {...rest}
    >
      <FieldLabel
        contextualHelp={contextualHelp}
        isDisabled={isDisabled}
        isRequired={isRequired}
      >
        {label}
      </FieldLabel>
      <DateRangePickerTrigger
        isDisabled={isDisabled}
        isLoading={isLoading}
        size={size}
      />
      <Popover
        UNSTABLE_portalContainer={root}
        className={clsx(dateRangePickerStyles.popover, popoverClassName)}
      >
        <Dialog>
          <RangeCalendar />
        </Dialog>
      </Popover>
      <FieldError>{errorMessage}</FieldError>
    </HeadlessDateRangePicker>
  );
};
